import { MODULE_SLUGS, MODULE_DEFINITIONS, type ModuleSlug, type ModuleStatusMap } from "@a2/core";
import { cn } from "../../lib/utils";
import { useT } from "../../lib/use-t";

type Props = {
  active: ModuleSlug;
  statusMap: ModuleStatusMap;
  onPick: (slug: ModuleSlug) => void;
  disabled?: boolean;
};

/** Horizontal chip row — the demoted module selector. `hidden` modules render
 *  nothing; `binnenkort` modules are disabled with a mono badge; `active`
 *  modules are selectable. Text-only chips (v1). */
export function ModuleChipRow({ active, statusMap, onPick, disabled }: Props) {
  const t = useT();
  return (
    <div className="-mx-5 sm:mx-0">
      <div
        className="flex gap-2 overflow-x-auto px-5 sm:px-0 sm:flex-wrap pb-1 sm:pb-0"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {MODULE_SLUGS.map((slug) => {
          const status = statusMap[slug];
          if (status === "hidden") return null;
          const def = MODULE_DEFINITIONS[slug];

          if (status === "binnenkort") {
            return (
              <span
                key={slug}
                aria-disabled="true"
                className="flex-none inline-flex items-center gap-2 rounded-full border-[1.5px] border-[var(--border)] bg-[var(--panel)] px-4 py-2 text-sm font-semibold text-[var(--ink-muted)] opacity-50 cursor-not-allowed"
              >
                {def.label}
                <span className="font-mono-badge text-[9px] uppercase tracking-wider text-[var(--ink-faint)]">
                  {t("oefenen.binnenkort")}
                </span>
              </span>
            );
          }

          const isActive = active === slug;
          return (
            <button
              key={slug}
              type="button"
              onClick={() => onPick(slug)}
              aria-pressed={isActive}
              disabled={disabled}
              className={cn(
                "flex-none inline-flex items-center rounded-full border-[1.5px] px-4 py-2 text-sm font-semibold transition-colors",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2",
                "disabled:cursor-not-allowed disabled:opacity-60",
                isActive
                  ? "border-[var(--accent)] bg-[var(--accent)] text-white shadow-[var(--shadow-blue)]"
                  : "border-[var(--border)] bg-[var(--panel)] text-[var(--ink-muted)] enabled:hover:border-[var(--border-strong)] enabled:hover:text-[var(--ink)]",
              )}
            >
              {def.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
